import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import AdminLayout from "../../../components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import type { RecommendedQuestion } from "../../../services/recommendedQuestion";
import {
  searchQuestions,
  editQuestion,
  batchDeleteQuestions,
  reorderQuestions,
} from "../../../services/recommendedQuestion";
import EnabledQuestionItem from "./components/EnabledQuestionItem";
import DisabledQuestionItem from "./components/DisabledQuestionItem";

// ─── Empty Slot ────────────────────────────────────────────────────────────────

function EmptySlotRow({ slot }: { slot: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100 last:border-b-0 group">
      <div className="w-6 h-6 flex-shrink-0" />
      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 border-2 border-dashed border-gray-300 text-gray-400">
        {slot}
      </div>
      <span className="flex-1 text-sm text-gray-400 italic">{t("rec.emptySlot")}</span>
      <Link
        to="/admin/recommended-questions/new"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-dashed border-gray-300 text-gray-400 hover:border-[#830051] hover:text-[#830051] cursor-pointer transition-colors whitespace-nowrap opacity-0 group-hover:opacity-100"
      >
        <i className="ri-add-line text-sm"></i>
        {t("rec.addQuestion")}
      </Link>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function RecommendedQuestionsPage() {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState<RecommendedQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 加载数据
  const fetchData = useCallback(async () => {
    try {
      const res = await searchQuestions();
      setQuestions(res.data.Data ?? []);
    } catch {
      setError(t("common.noDataFound"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeQuestions = questions
    .filter((q) => q.IsEnabled)
    .sort((a, b) => a.DisplayOrder - b.DisplayOrder);

  const inactiveQuestions = questions.filter((q) => !q.IsEnabled);

  const isFull = activeQuestions.length >= 6;
  const emptySlots = Math.max(0, 6 - activeQuestions.length);

  // 拖拽排序
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = activeQuestions.findIndex((q) => q.ID === active.id);
    const newIndex = activeQuestions.findIndex((q) => q.ID === over.id);
    const reordered = arrayMove(activeQuestions, oldIndex, newIndex);
    const newIds = reordered.map((q) => q.ID);

    // 乐观更新
    setQuestions((prev) => {
      const updated = [...prev];
      reordered.forEach((q, idx) => {
        const target = updated.find((u) => u.ID === q.ID);
        if (target) target.DisplayOrder = idx + 1;
      });
      return updated;
    });

    try {
      await reorderQuestions(newIds);
    } catch {
      fetchData();
    }
  };

  // 禁用问题
  const handleDisable = async (id: string) => {
    const q = questions.find((q) => q.ID === id);
    if (!q) return;

    try {
      await editQuestion({ ID: id, Question: q.Question, IsEnabled: false });
      fetchData();
    } catch {
      setError(t("rec.operationFailed"));
    }
  };

  // 启用问题
  const handleEnable = async (id: string) => {
    if (isFull) {
      setError(t("rec.maxEnabledHint"));
      setTimeout(() => setError(""), 3000);
      return;
    }
    const q = questions.find((q) => q.ID === id);
    if (!q) return;

    try {
      await editQuestion({ ID: id, Question: q.Question, IsEnabled: true });
      fetchData();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || t("rec.operationFailed"));
      setTimeout(() => setError(""), 3000);
    }
  };

  // 删除问题
  const handleDelete = async (id: string) => {
    try {
      await batchDeleteQuestions([id]);
      setQuestions((prev) => prev.filter((q) => q.ID !== id));
    } catch {
      setError(t("rec.operationFailed"));
      setTimeout(() => setError(""), 3000);
    }
  };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-7">
          <div>
            <AdminPageTitle title={t("rec.title")} tooltip={t("rec.simpleSubtitle")} />
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/recommended-questions/new"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer whitespace-nowrap transition-opacity ${isFull ? "opacity-40 pointer-events-none" : "hover:opacity-90"}`}
              style={{ backgroundColor: "#830051" }}
            >
              <i className="ri-add-line text-base"></i>
              {t("rec.newQuestion")}
            </Link>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 text-base"></i>
            <span className="text-sm text-red-600">{error}</span>
            <button
              type="button"
              onClick={() => setError("")}
              className="ml-auto text-red-400 hover:text-red-600 cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        )}

        {/* 加载状态 */}
        {loading ? (
          <div className="py-20 text-center text-gray-400">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
            <p className="text-sm mt-2">{t("common.loading")}</p>
          </div>
        ) : (
          <>
            {/* ── Section 1: Enabled Questions ──────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-5">
              {/* Section header */}
              <div
                className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3"
                style={{ backgroundColor: "rgba(131,0,81,0.04)" }}
              >
                <div
                  className="w-5 h-5 flex items-center justify-center"
                  style={{ color: "#830051" }}
                >
                  <i className="ri-list-ordered text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">
                  {t("rec.enabledSection")}
                </span>
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: "#F8ECF3", color: "#830051" }}
                >
                  {activeQuestions.length}/6
                </span>
                <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-drag-move-2-fill text-xs"></i>
                  {t("rec.dragToReorder")}
                </span>
              </div>

              {/* Sortable list */}
              {activeQuestions.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={activeQuestions.map((q) => q.ID)}
                    strategy={verticalListSortingStrategy}
                  >
                    {activeQuestions.map((q, idx) => (
                      <EnabledQuestionItem
                        key={q.ID}
                        question={q}
                        slot={idx + 1}
                        onDisable={handleDisable}
                        onDelete={handleDelete}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : null}

              {/* Empty slots */}
              {Array.from({ length: emptySlots }).map((_, i) => (
                <EmptySlotRow key={`empty-${i}`} slot={activeQuestions.length + i + 1} />
              ))}

              {/* Bottom hint */}
              {activeQuestions.length > 0 && (
                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-information-line text-gray-400 text-xs"></i>
                  </div>
                  <p className="text-xs text-gray-400">{t("rec.enabledHint")}</p>
                </div>
              )}

              {/* All empty state */}
              {activeQuestions.length === 0 && (
                <div className="py-10 text-center text-gray-400">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-question-answer-line text-3xl text-gray-300"></i>
                  </div>
                  <p className="text-sm">{t("rec.noEnabledQuestions")}</p>
                </div>
              )}
            </div>

            {/* ── Section 2: Disabled Questions ─────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {/* Section header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3 bg-gray-50/60">
                <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-eye-off-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-gray-600">
                  {t("rec.disabledSection")}
                </span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  {inactiveQuestions.length}
                </span>
                {isFull && (
                  <span className="ml-auto text-xs text-amber-600 flex items-center gap-1">
                    <i className="ri-lock-line text-xs"></i>
                    {t("rec.maxEnabledHint")}
                  </span>
                )}
              </div>

              {/* Disabled list */}
              {inactiveQuestions.length > 0 ? (
                inactiveQuestions.map((q) => (
                  <DisabledQuestionItem
                    key={q.ID}
                    question={q}
                    canEnable={!isFull}
                    onEnable={handleEnable}
                    onDelete={handleDelete}
                  />
                ))
              ) : (
                <div className="py-10 text-center text-gray-400">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-check-double-line text-3xl text-gray-300"></i>
                  </div>
                  <p className="text-sm">{t("rec.noDisabledQuestions")}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
